import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  createTennisAcademyTariff,
  deactivateTennisAcademyTariff,
  getTennisAcademyApplications,
  getTennisAcademyTariffs,
  updateTennisAcademyApplicationStatus,
  updateTennisAcademyTariff,
  getTennisAcademyPaymentHistory
} from '../../../services/tennisAcademyService';
import { isAdminUser } from '../../../services/authService';
import { paymentDashboardService } from '../../../services/paymentDashboardService';
import { Modal, message, Input } from 'antd';
import { FaFileExcel } from 'react-icons/fa';
import './TennisAcademyManagement.scss';

const isDevelopment = process.env.NODE_ENV === 'development';

const initialTariffForm = {
  tariffCode: '',
  tariffName: '',
  amount: '',
  currency: 'TRY',
  description: '',
  isActive: true,
  sortOrder: 0
};

const statusLabelMap = {
  PendingPayment: 'Ödeme Bekliyor',
  Pending: 'Yeni Başvuru',
  InReview: 'İncelemede',
  Approved: 'Onaylandı',
  Rejected: 'Reddedildi',
  Cancelled: 'İptal'
};

const statusBadgeMap = {
  PendingPayment: 'pending',
  Pending: 'pending',
  InReview: 'active',
  Approved: 'confirmed',
  Rejected: 'cancelled',
  Cancelled: 'cancelled'
};

const paymentStatusLabelMap = {
  approved: 'Onaylandı',
  pending: 'Beklemede',
  failed: 'Ödeme Alınamadı',
  error: 'Ödeme Alınamadı',
  declined: 'Ödeme Alınamadı',
  cancelled: 'İptal',
  refunded: 'İade Edildi'
};

const failedPaymentStatuses = new Set(['failed', 'error', 'declined']);

const applicationStatusPriority = {
  Approved: 0,
  PendingPayment: 1,
  Pending: 2,
  InReview: 3,
  Rejected: 4,
  Cancelled: 5
};

const getTimestamp = (...dateValues) => {
  for (const rawDate of dateValues) {
    if (!rawDate) continue;
    const timestamp = new Date(rawDate).getTime();
    if (!Number.isNaN(timestamp)) {
      return timestamp;
    }
  }

  return 0;
};

const getApplicationSortTimestamp = (application) => {
  if (application?.status === 'Approved') {
    return getTimestamp(
      application.membershipEndDate,
      application.updatedDate,
      application.reviewedDate,
      application.createdDate,
      application.createdAt,
      application.registrationDate
    );
  }

  return getTimestamp(
    application.updatedDate,
    application.reviewedDate,
    application.createdDate,
    application.createdAt,
    application.registrationDate
  );
};

const TennisAcademyManagement = ({ showToast }) => {
  const isAdmin = isAdminUser();
  const [tariffs, setTariffs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loadingTariffs, setLoadingTariffs] = useState(false);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [savingTariff, setSavingTariff] = useState(false);
  const [editingTariffId, setEditingTariffId] = useState(null);
  const [tariffForm, setTariffForm] = useState(initialTariffForm);
  const [nameSearch, setNameSearch] = useState('');
  const [tariffFilter, setTariffFilter] = useState('all');
  const [membershipFilter, setMembershipFilter] = useState('all'); // 'all' | 'active' | 'passive'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exportingPayments, setExportingPayments] = useState(false);
  const [paymentHistoryModal, setPaymentHistoryModal] = useState({ open: false, application: null, history: [], loading: false });

  const loadTariffs = useCallback(async () => {
    setLoadingTariffs(true);
    try {
      const res = await getTennisAcademyTariffs(true);
      if (res?.isSuccess) {
        setTariffs(res.data || []);
      }
    } catch (error) {
      console.error('Tenis akademi tarifeleri alınamadı:', error);
    } finally {
      setLoadingTariffs(false);
    }
  }, []);

  const loadApplications = useCallback(async () => {
    setLoadingApplications(true);
    try {
      const res = await getTennisAcademyApplications('all');
      if (res?.isSuccess) {
        setApplications(res.data || []);
      }
    } catch (error) {
      console.error('Tenis akademi başvuruları alınamadı:', error);
    } finally {
      setLoadingApplications(false);
    }
  }, []);

  useEffect(() => {
    loadTariffs();
  }, [loadTariffs]);

  useEffect(() => {
    loadApplications();
  }, [loadApplications]);

  const filteredApplications = useMemo(() => {
    const now = new Date();
    return applications
      .filter((app) => {
        if (nameSearch.trim()) {
          const fullName = `${app.firstName || ''} ${app.lastName || ''}`.toLowerCase();
          const tcSearch = (app.identityNumber || '').toLowerCase();
          const search = nameSearch.trim().toLowerCase();
          if (!fullName.includes(search) && !tcSearch.includes(search)) return false;
        }
        if (tariffFilter !== 'all') {
          if ((app.tariffName || '') !== tariffFilter) return false;
        }
        if (membershipFilter !== 'all' && app.status === 'Approved') {
          const endDate = app.membershipEndDate ? new Date(app.membershipEndDate) : null;
          const isActive = endDate && endDate > now;
          if (membershipFilter === 'active' && !isActive) return false;
          if (membershipFilter === 'passive' && isActive) return false;
        } else if (membershipFilter !== 'all' && app.status !== 'Approved') {
          if (membershipFilter === 'active') return false;
        }
        if (dateFrom) {
          const appDate = app.createdDate ? new Date(app.createdDate) : null;
          if (!appDate || appDate < new Date(dateFrom)) return false;
        }
        if (dateTo) {
          const appDate = app.createdDate ? new Date(app.createdDate) : null;
          const toDate = new Date(dateTo);
          toDate.setHours(23, 59, 59, 999);
          if (!appDate || appDate > toDate) return false;
        }
        return true;
      })
      .sort((a, b) => {
        const statusDiff =
          (applicationStatusPriority[a.status] ?? Number.MAX_SAFE_INTEGER) -
          (applicationStatusPriority[b.status] ?? Number.MAX_SAFE_INTEGER);
        if (statusDiff !== 0) return statusDiff;

        const timeDiff = getApplicationSortTimestamp(b) - getApplicationSortTimestamp(a);
        if (timeDiff !== 0) return timeDiff;

        const createdDiff = getTimestamp(b.createdDate, b.createdAt) - getTimestamp(a.createdDate, a.createdAt);
        if (createdDiff !== 0) return createdDiff;

        return (b.applicationId || 0) - (a.applicationId || 0);
      });
  }, [applications, nameSearch, tariffFilter, membershipFilter, dateFrom, dateTo]);

  const uniqueTariffNames = useMemo(() => {
    const names = new Set(applications.map((a) => a.tariffName).filter(Boolean));
    return Array.from(names).sort();
  }, [applications]);

  const handleTariffSubmit = async (event) => {
    event.preventDefault();

    if (!tariffForm.tariffName.trim()) {
      message.error('Tarife adı zorunludur.');
      return;
    }

    const parsedAmount = Number(tariffForm.amount);
    if (Number.isNaN(parsedAmount) || parsedAmount < 0) {
      message.error('Geçerli bir ücret giriniz.');
      return;
    }

    Modal.confirm({
      title: 'Onay',
      content: editingTariffId ? 'Bu tarifeyi güncellemek istediğinize emin misiniz?' : 'Yeni tarife eklemek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: async () => {
        if (!isAdmin) {
          if (showToast) {
            showToast('Bu işlem için yöneticiniz ile görüşün.', 'error');
          } else {
            message.error('Bu işlem için yöneticiniz ile görüşün.');
          }
          return;
        }

        setSavingTariff(true);
        try {
          const payload = {
            tariffCode: tariffForm.tariffCode.trim() || null,
            tariffName: tariffForm.tariffName.trim(),
            amount: parsedAmount,
            currency: (tariffForm.currency || 'TRY').trim().toUpperCase(),
            description: tariffForm.description.trim() || null,
            isActive: Boolean(tariffForm.isActive),
            sortOrder: Number(tariffForm.sortOrder) || 0
          };

          const response = editingTariffId
            ? await updateTennisAcademyTariff(editingTariffId, payload)
            : await createTennisAcademyTariff(payload);

          if (!response?.isSuccess) {
            if (showToast) {
              showToast(response?.resultMessage || 'Tarife kaydedilemedi.', 'error');
            } else {
              message.error(response?.resultMessage || 'Tarife kaydedilemedi.');
            }
            return;
          }

          setTariffForm(initialTariffForm);
          setEditingTariffId(null);
          await loadTariffs();
          if (showToast) {
            showToast('Tarife başarıyla kaydedildi.', 'success');
          } else {
            message.success('Tarife başarıyla kaydedildi.');
          }
        } catch (error) {
          console.error('Tarife kayıt hatası:', error);
          if (showToast) {
            showToast('Tarife kaydedilemedi.', 'error');
          } else {
            message.error('Tarife kaydedilemedi.');
          }
        } finally {
          setSavingTariff(false);
        }
      }
    });
  };

  const handleEditTariff = (tariff) => {
    Modal.confirm({
      title: 'Düzenleme Onayı',
      content: 'Bu tarifeyi düzenlemek istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: () => {
        if (!isAdmin) {
          if (showToast) {
            showToast('Bu işlem için yöneticiniz ile görüşün.', 'error');
          } else {
            message.error('Bu işlem için yöneticiniz ile görüşün.');
          }
          return;
        }
        setEditingTariffId(tariff.tariffId);
        setTariffForm({
          tariffCode: tariff.tariffCode || '',
          tariffName: tariff.tariffName || '',
          amount: tariff.amount ?? '',
          currency: tariff.currency || 'TRY',
          description: tariff.description || '',
          isActive: Boolean(tariff.isActive),
          sortOrder: tariff.sortOrder ?? 0
        });
      }
    });
  };

  const handleDeactivateTariff = async (tariffId) => {
    Modal.confirm({
      title: 'Onay',
      content: 'Bu tarifeyi pasif yapmak istediğinize emin misiniz?',
      okText: 'Evet',
      cancelText: 'Hayır',
      onOk: async () => {
        if (!isAdmin) {
          if (showToast) {
            showToast('Bu işlem için yöneticiniz ile görüşün.', 'error');
          } else {
            message.error('Bu işlem için yöneticiniz ile görüşün.');
          }
          return;
        }

        try {
          const response = await deactivateTennisAcademyTariff(tariffId);
          if (!response?.isSuccess) {
            if (showToast) {
              showToast(response?.resultMessage || 'Tarife pasif yapılamadı.', 'error');
            } else {
              message.error(response?.resultMessage || 'Tarife pasif yapılamadı.');
            }
            return;
          }

          await loadTariffs();
          if (showToast) {
            showToast('Tarife pasif yapıldı.', 'success');
          } else {
            message.success('Tarife pasif yapıldı.');
          }
        } catch (error) {
          console.error('Tarife pasifleştirme hatası:', error);
          if (showToast) {
            showToast('Tarife pasif yapılamadı.', 'error');
          } else {
            message.error('Tarife pasif yapılamadı.');
          }
        }
      }
    });
  };

  const handleOpenPaymentHistory = async (application) => {
    setPaymentHistoryModal({ open: true, application, history: [], loading: true });
    try {
      const res = await getTennisAcademyPaymentHistory(application.applicationId);
      setPaymentHistoryModal((prev) => ({
        ...prev,
        loading: false,
        history: res?.isSuccess ? (res.data || []) : []
      }));
    } catch {
      setPaymentHistoryModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleExportAllPayments = async () => {
    if (exportingPayments) return;

    setExportingPayments(true);
    try {
      const fileBlob = await paymentDashboardService.downloadTransactionsExcel({ type: 'tennis' });
      const downloadUrl = window.URL.createObjectURL(fileBlob);
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

      link.href = downloadUrl;
      link.download = `Tenis_Akademisi_Tum_Odemeler_${timestamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      message.success('Tüm Tenis Akademisi ödeme geçmişi Excel olarak indirildi.');
    } catch (error) {
      console.error('Tenis akademisi ödeme geçmişi Excel indirme hatası:', error);
      message.error('Ödeme geçmişi Excel dosyası indirilemedi.');
    } finally {
      setExportingPayments(false);
    }
  };

  const handleUpdateStatus = async (applicationId, status) => {
    let adminNotesValue = '';
    Modal.confirm({
      title: status === 'Approved' ? 'Başvuru Onayı' : 'Başvuru Reddi',
      content: (
        <div style={{ marginTop: '10px' }}>
          <p>İsteğe bağlı not giriniz (boş bırakabilirsiniz):</p>
          <Input 
            autoFocus
            onChange={(e) => { adminNotesValue = e.target.value; }} 
            placeholder="Notunuz..."
          />
        </div>
      ),
      okText: 'Tamam',
      cancelText: 'İptal',
      onOk: async () => {
        if (!isAdmin) {
          if (showToast) {
            showToast('Bu işlem için yöneticiniz ile görüşün.', 'error');
          } else {
            message.error('Bu işlem için yöneticiniz ile görüşün.');
          }
          return;
        }

        try {
          const response = await updateTennisAcademyApplicationStatus(applicationId, { status, adminNotes: adminNotesValue });
          if (!response?.isSuccess) {
            if (showToast) {
              showToast(response?.resultMessage || 'Durum güncellenemedi.', 'error');
            } else {
              message.error(response?.resultMessage || 'Durum güncellenemedi.');
            }
            return;
          }

          await loadApplications();
          if (showToast) {
            showToast('Durum güncellendi.', 'success');
          } else {
            message.success('Durum güncellendi.');
          }
        } catch (error) {
          console.error('Başvuru durum güncelleme hatası:', error);
          if (showToast) {
            showToast('Durum güncellenemedi.', 'error');
          } else {
            message.error('Durum güncellenemedi.');
          }
        }
      }
    });
  };

  return (
    <div className="ta-admin">
      <div className="appointments-list-section ta-admin-section">
        <div className="section-header">
          <h3>Tenis Akademi Tarifeleri</h3>
        </div>

        {/* Tarife form — görünür ama rol yetkisi submissionı engelleyecek */}
        <div className="ta-admin-form-area">
          <form onSubmit={handleTariffSubmit} className="ta-admin-form-grid">
            <div className="ta-admin-field">
              <label className="ta-admin-label">Tarife Kodu</label>
              <input
                value={tariffForm.tariffCode}
                onChange={(event) => setTariffForm((prev) => ({ ...prev, tariffCode: event.target.value }))}
                className="ta-admin-input"
                placeholder="ECO"
              />
            </div>
            <div className="ta-admin-field">
              <label className="ta-admin-label">Tarife Adı *</label>
              <input
                required
                value={tariffForm.tariffName}
                onChange={(event) => setTariffForm((prev) => ({ ...prev, tariffName: event.target.value }))}
                className="ta-admin-input"
                placeholder="Eco Paket"
              />
            </div>
            <div className="ta-admin-field">
              <label className="ta-admin-label">Ücret *</label>
              <input
                required
                type="number"
                min="0"
                step="0.01"
                value={tariffForm.amount}
                onChange={(event) => setTariffForm((prev) => ({ ...prev, amount: event.target.value }))}
                className="ta-admin-input"
                placeholder="300"
              />
            </div>
            <div className="ta-admin-field">
              <label className="ta-admin-label">Sıralama</label>
              <input
                type="number"
                value={tariffForm.sortOrder}
                onChange={(event) => setTariffForm((prev) => ({ ...prev, sortOrder: event.target.value }))}
                className="ta-admin-input"
              />
            </div>
            <div className="ta-admin-switch-wrap">
              <input
                id="tennis-tariff-active"
                type="checkbox"
                checked={tariffForm.isActive}
                onChange={(event) => setTariffForm((prev) => ({ ...prev, isActive: event.target.checked }))}
              />
              <label htmlFor="tennis-tariff-active" className="ta-admin-switch-label">Aktif</label>
            </div>
            <div className="ta-admin-form-actions">
              <button type="submit" className="ta-admin-btn ta-admin-btn-primary" disabled={savingTariff}>
                {savingTariff ? 'Kaydediliyor...' : editingTariffId ? 'Güncelle' : 'Ekle'}
              </button>
              {editingTariffId && (
                <button
                  type="button"
                  className="ta-admin-btn ta-admin-btn-secondary"
                  onClick={() => {
                    setEditingTariffId(null);
                    setTariffForm(initialTariffForm);
                  }}
                >
                  Vazgeç
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="appointments-table-wrapper">
          <table className="appointments-table">
            <thead>
              <tr>
                <th>KOD</th>
                <th>TARİFE</th>
                <th>ÜCRET</th>
                <th>DURUM</th>
                <th>SIRA</th>
                <th>İŞLEM</th>
              </tr>
            </thead>
            <tbody>
              {loadingTariffs ? (
                <tr>
                  <td colSpan="6" className="no-data">Tarifeler yükleniyor...</td>
                </tr>
              ) : tariffs.length === 0 ? (
                <tr>
                  <td colSpan="6" className="no-data">Henüz tarife bulunmuyor.</td>
                </tr>
              ) : (
                tariffs.map((tariff) => (
                  <tr key={tariff.tariffId}>
                    <td data-label="KOD">{tariff.tariffCode || '-'}</td>
                    <td className="name-cell" data-label="TARİFE">{tariff.tariffName}</td>
                    <td data-label="ÜCRET">{Number(tariff.amount || 0).toFixed(2)} {tariff.currency || 'TRY'}</td>
                    <td data-label="DURUM">
                      <span className={`status-badge ${tariff.isActive ? 'confirmed' : 'cancelled'}`}>
                        {tariff.isActive ? 'AKTİF' : 'PASİF'}
                      </span>
                    </td>
                    <td data-label="SIRA">{tariff.sortOrder ?? 0}</td>
                    <td data-label="İŞLEM">
                      <div className="appointment-actions">
                          <button type="button" className="action-button detail" onClick={() => handleEditTariff(tariff)}>
                            Düzenle
                          </button>
                        {tariff.isActive && (
                          <button type="button" className="action-button cancel" onClick={() => handleDeactivateTariff(tariff.tariffId)}>
                            Pasifleştir
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="appointments-list-section ta-admin-section">
        <div className="section-header">
          <h3>Tenis Akademi Başvuruları</h3>
          <div className="ta-admin-header-actions">
            <span className="appointment-count">{filteredApplications.length} kayıt</span>
            {isDevelopment && (
              <button
                type="button"
                className="ta-admin-excel-btn"
                onClick={handleExportAllPayments}
                disabled={exportingPayments}
                title="Tüm geçmiş Tenis Akademisi ödemelerini indir"
              >
                <FaFileExcel aria-hidden="true" />
                {exportingPayments ? 'Hazırlanıyor...' : 'Tüm Ödemeler Excel'}
              </button>
            )}
          </div>
        </div>

        <div className="ta-admin-filters">
          <div className="filter-group main-search">
            <input
              type="text"
              className="ta-admin-input search-input"
              placeholder="İsim veya TC ile ara..."
              value={nameSearch}
              onChange={(e) => setNameSearch(e.target.value)}
            />
          </div>
          
          <div className="filter-group-row">
            <div className="filter-group date-filters">
              <input
                type="date"
                className="ta-admin-input"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                title="Başlangıç tarihi"
              />
              <span className="ta-admin-date-sep">–</span>
              <input
                type="date"
                className="ta-admin-input"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                title="Bitiş tarihi"
              />
            </div>
            
            <div className="filter-group select-filters">
              <select
                className="ta-admin-input"
                value={tariffFilter}
                onChange={(e) => setTariffFilter(e.target.value)}
              >
                <option value="all">Tüm Tarifeler</option>
                {uniqueTariffNames.map((name) => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>

              <select
                className="ta-admin-input"
                value={membershipFilter}
                onChange={(e) => setMembershipFilter(e.target.value)}
              >
                <option value="all">Tüm Üyeler</option>
                <option value="active">Aktif Üyeler</option>
                <option value="passive">Pasif / Süresi Dolan</option>
              </select>
            </div>
          </div>
        </div>

        <div className="appointments-table-wrapper">
          <table className="appointments-table">
            <thead>
              <tr>
                <th>AD SOYAD</th>
                <th>TARİFE</th>
                <th>TESİS</th>
                <th>TUTAR</th>
                <th>TC</th>
                <th>TELEFON</th>
                <th>ÜYELİK BİTİŞ</th>
                <th>DURUM</th>
                <th>ÖDEME GEÇMİŞİ</th>
              </tr>
            </thead>
            <tbody>
              {loadingApplications ? (
                <tr>
                  <td colSpan="9" className="no-data">Başvurular yükleniyor...</td>
                </tr>
              ) : filteredApplications.length === 0 ? (
                <tr>
                  <td colSpan="9" className="no-data">Kayıt bulunamadı.</td>
                </tr>
              ) : (
                filteredApplications.map((application) => {
                  const now = new Date();
                  const endDate = application.membershipEndDate ? new Date(application.membershipEndDate) : null;
                  const isActiveMember = application.status === 'Approved' && endDate && endDate > now;
                  const paymentKey = (application.paymentStatus || '').toLowerCase();
                  const paymentLabel = paymentStatusLabelMap[paymentKey] || application.paymentStatus || '-';
                  return (
                    <tr key={application.applicationId}>
                      <td className="name-cell" data-label="AD SOYAD">
                        <span className="value">{application.firstName} {application.lastName}</span>
                        <small style={{ display: 'block', color: '#94a3b8', fontSize: '11px' }}>{application.identityNumber}</small>
                      </td>
                      <td data-label="TARİFE"><span className="value">{application.tariffName || '-'}</span></td>
                      <td data-label="TESİS"><span className="value">{application.facilityName || '-'}</span></td>
                      <td data-label="TUTAR"><span className="value">{Number(application.amount || 0).toFixed(2)} {application.currency || 'TRY'}</span></td>
                      <td data-label="TC"><span className="value">{application.identityNumber || '-'}</span></td>
                      <td data-label="TELEFON"><span className="value">{application.phone || '-'}</span></td>
                      <td data-label="ÜYELİK BİTİŞ">
                        <span className="value" style={{ color: isActiveMember ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                          {endDate ? endDate.toLocaleDateString('tr-TR') : '-'}
                        </span>
                        {application.status === 'Approved' && (
                          <small style={{ display: 'block', fontSize: '11px', color: isActiveMember ? '#10b981' : '#ef4444' }}>
                            {isActiveMember ? 'Aktif' : 'Süresi Dolmuş'}
                          </small>
                        )}
                      </td>
                      <td data-label="DURUM">
                        <span className={`status-badge ${statusBadgeMap[application.status] || 'pending'}`}>
                          {statusLabelMap[application.status] || application.status}
                        </span>
                      </td>
                      <td className="appointment-actions-cell" data-label="ÖDEME GEÇMİŞİ">
                        <button
                          type="button"
                          className="action-button detail"
                          onClick={() => handleOpenPaymentHistory(application)}
                        >
                          Ödemeler
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ödeme Geçmişi Modal */}
      <Modal
        open={paymentHistoryModal.open}
        title={paymentHistoryModal.application ? `${paymentHistoryModal.application.firstName} ${paymentHistoryModal.application.lastName} — Ödeme Geçmişi` : 'Ödeme Geçmişi'}
        footer={null}
        width={700}
        onCancel={() => setPaymentHistoryModal({ open: false, application: null, history: [], loading: false })}
      >
        {paymentHistoryModal.loading ? (
          <div style={{ textAlign: 'center', padding: '30px' }}>Yükleniyor...</div>
        ) : paymentHistoryModal.history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>Bu başvuruya ait ödeme işlemi bulunamadı.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            {paymentHistoryModal.application && (
              <div style={{ marginBottom: '16px', padding: '12px 16px', background: '#f8fafc', borderRadius: '8px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                <div><strong>TC:</strong> {paymentHistoryModal.application.identityNumber}</div>
                <div><strong>Tarife:</strong> {paymentHistoryModal.application.tariffName}</div>
                <div><strong>Üyelik Bitişi:</strong> {paymentHistoryModal.application.membershipEndDate ? new Date(paymentHistoryModal.application.membershipEndDate).toLocaleDateString('tr-TR') : '-'}</div>
              </div>
            )}
            <table className="appointments-table" style={{ fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={{ padding: '12px 16px' }}>İŞLEM ZAMANI</th>
                  <th style={{ padding: '12px 16px' }}>TUTAR</th>
                  <th style={{ padding: '12px 16px' }}>AÇIKLAMA</th>
                  <th style={{ padding: '12px 16px' }}>DURUM</th>
                </tr>
              </thead>
              <tbody>
                {paymentHistoryModal.history.map((p, idx) => {
                  const pKey = (p.status || '').toLowerCase();
                  const isFailed = failedPaymentStatuses.has(pKey);
                  const displayCurrency = p.currency === '949' ? 'TL' : (p.currency || 'TL');
                  return (
                    <tr key={p.transactionId || idx}>
                      <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>{p.createdAt ? new Date(p.createdAt).toLocaleString('tr-TR') : '-'}</td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, whiteSpace: 'nowrap' }}>{Number(p.amount || 0).toFixed(2)} {displayCurrency}</td>
                      <td style={{ padding: '12px 16px', color: '#64748b', fontSize: '12px' }}>{p.description || '-'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <span className={`payment-badge ${isFailed ? 'payment-failed' : pKey}`} title={p.errMsg || undefined}>
                          {paymentStatusLabelMap[pKey] || p.status || '-'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TennisAcademyManagement;
